import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Sparkles, ArrowRight, X, UserPlus, MessageCircleHeart } from "lucide-react";

const API = "/api";
const DISMISS_KEY = "evoluras.invite_suggestions.dismissed";

const CATEGORY_COLOR = {
  strengths: { bg: "bg-emerald-50", border: "border-emerald-200/60", text: "text-emerald-700", chip: "bg-emerald-100 text-emerald-700" },
  weaknesses: { bg: "bg-rose-50", border: "border-rose-200/60", text: "text-rose-700", chip: "bg-rose-100 text-rose-700" },
  opportunities: { bg: "bg-sky-50", border: "border-sky-200/60", text: "text-sky-700", chip: "bg-sky-100 text-sky-700" },
  threats: { bg: "bg-amber-50", border: "border-amber-200/60", text: "text-amber-700", chip: "bg-amber-100 text-amber-700" },
};

const ROLE_LABELS = {
  mentor: "Mentor", coach: "Coach", coworker: "Coworker",
  business_partner: "Business Partner", therapist: "Therapist",
  friend: "Friend", family: "Family",
};

/**
 * Context-aware prompt banner: when the user has recent SWOT items with no
 * insight requests, suggests inviting or asking a matching connection.
 */
const InviteSuggestionsBanner = ({ onInvite, onAsk }) => {
  const navigate = useNavigate();
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(() => {
    try {
      return new Set(JSON.parse(localStorage.getItem(DISMISS_KEY) || "[]"));
    } catch {
      return new Set();
    }
  });

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        const res = await fetch(`${API}/network/invite-suggestions`, { credentials: "include" });
        if (!res.ok) return;
        const d = await res.json();
        if (!cancelled) setSuggestions(d.suggestions || []);
      } catch {} finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => { cancelled = true; };
  }, []);

  const dismiss = (itemId) => {
    const next = new Set(dismissed);
    next.add(itemId);
    setDismissed(next);
    localStorage.setItem(DISMISS_KEY, JSON.stringify([...next]));
  };

  if (loading) return null;
  const visible = suggestions.filter((s) => !dismissed.has(s.item_id));
  if (visible.length === 0) return null;

  const primary = visible[0];
  const color = CATEGORY_COLOR[primary.category] || CATEGORY_COLOR.strengths;

  return (
    <div
      className={`relative rounded-2xl border ${color.bg} ${color.border} p-4 sm:p-5 mb-6 overflow-hidden`}
      data-testid="invite-suggestions-banner"
    >
      <button
        onClick={() => dismiss(primary.item_id)}
        className="absolute top-2.5 right-2.5 p-1 rounded-full text-slate-400 hover:bg-white/60 hover:text-slate-600 transition"
        data-testid="suggestion-dismiss"
        title="Not now"
      >
        <X className="w-3.5 h-3.5" />
      </button>

      <div className="flex items-start gap-3 sm:gap-4">
        <div className={`flex-shrink-0 w-9 h-9 rounded-full ${color.chip} flex items-center justify-center`}>
          <Sparkles className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0 pr-6">
          <p className={`text-[11px] font-semibold uppercase tracking-wide ${color.text} mb-1`}>
            You recently added a {primary.category.slice(0, -1)}
          </p>
          <p className="text-sm sm:text-base text-slate-900 font-medium leading-snug mb-1">
            &ldquo;{primary.item_text}&rdquo;
          </p>
          <p className="text-xs sm:text-sm text-slate-600 leading-snug">
            Why not {primary.prompt}?
          </p>

          <div className="flex items-center gap-2 mt-3 flex-wrap">
            {primary.has_matching_connection ? (
              <Button
                onClick={() => onAsk?.(primary)}
                size="sm"
                className="bg-violet-600 hover:bg-violet-700 text-white rounded-full h-8 text-xs"
                data-testid="suggestion-ask-btn"
              >
                <MessageCircleHeart className="w-3.5 h-3.5 mr-1.5" />
                Ask for an insight
                <ArrowRight className="w-3.5 h-3.5 ml-1.5" />
              </Button>
            ) : (
              <Button
                onClick={() => onInvite?.(primary)}
                size="sm"
                className="bg-violet-600 hover:bg-violet-700 text-white rounded-full h-8 text-xs"
                data-testid="suggestion-invite-btn"
              >
                <UserPlus className="w-3.5 h-3.5 mr-1.5" />
                Invite a {ROLE_LABELS[primary.suggested_roles[0]] || "person"}
              </Button>
            )}
            <Button
              onClick={() => navigate("/tools/swot")}
              size="sm"
              variant="ghost"
              className="text-slate-500 hover:text-slate-700 rounded-full h-8 text-xs"
              data-testid="suggestion-view-swot"
            >
              View SWOT
            </Button>
            {visible.length > 1 && (
              <span className="text-[11px] text-slate-400 ml-auto">
                +{visible.length - 1} more suggestion{visible.length - 1 === 1 ? "" : "s"}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InviteSuggestionsBanner;
